import { CampaignSettingsMap } from 'components/Campaign/Settings/types';
import { DraftIssueFragmentData } from 'graphql/types';
import { createContext, useContext } from 'react';
import { CampaignStep } from '../useGetOrderedCampaignsSteps';

export const DEFAULT_CONTEXT_CAMPAIGN_ID = 'default-campaign-context-id';

export interface StepStatus {
  touched: boolean;
  completed: boolean;
  hasBlockingIssues: boolean;
  hasNonBlockingIssues: boolean;
}

export interface CampaignContextValue {
  isDraft: boolean;
  campaignId: string;
  goal: string | null;
  advertiserId: string;
  advertiserPrettyId: string;
  isPerformance: boolean;
  campaignPrettyId: string;
  hasBlockingIssues: boolean;
  blocksLaunchMissingFacebookPage: boolean;
  settings: CampaignSettingsMap;
  externalCampaignId: string | null;
  draftIssues: DraftIssueFragmentData[];
  shouldUseGoogleTagManager: boolean | null;
  campaignSteps: PartialRecord<CampaignStep, StepStatus>;
  campaignURL: string;
  trackerInstallStatus: string;
}

// the value to instance the context. will be overwritten when calling `Context.Provider`
const defaultValue: CampaignContextValue = {
  settings: {},
  isDraft: false,
  draftIssues: [],
  campaignSteps: {},
  isPerformance: false,
  hasBlockingIssues: false,
  blocksLaunchMissingFacebookPage: false,
  shouldUseGoogleTagManager: false,
  goal: 'default-campaign-context-goal',
  campaignId: DEFAULT_CONTEXT_CAMPAIGN_ID,
  externalCampaignId: DEFAULT_CONTEXT_CAMPAIGN_ID,
  advertiserId: 'default-campaign-advertiser-context-id',
  advertiserPrettyId: 'default-campaign-advertiser-context-pretty-id',
  campaignPrettyId: 'default-campaign-context-pretty-id',
  campaignURL: 'default-campaign-context-website',
  trackerInstallStatus: 'not-requested',
};

export const CampaignContext = createContext<CampaignContextValue>(defaultValue);

export const useCampaign = () => {
  const context = useContext(CampaignContext);

  if (context === undefined) {
    throw new Error('useCampaign must be used within a CampaignProvider');
  }

  return context;
};
